import { DialogTitle } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ZodSchema, z } from 'zod';
import FormInput from '@/components/common/form-input';
import Modal from '@/components/common/modal/modal';
import type { IncomingFormPrompt, Prompt } from '@/stores/prompter';

interface Props {
  prompt: Prompt & IncomingFormPrompt;
}

export default function FormPrompt({ prompt }: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm({
    resolver: zodResolver(prompt.meta.schema),
    defaultValues: prompt.meta.defaultValues,
  });

  return (
    <Modal
      key={prompt.key}
      defaultOpen
      clickOutsideToClose
      onClose={() => prompt.resolvers.resolve(false)}>
      <DialogTitle className='my-4 text-base font-bold'>{prompt.title}</DialogTitle>

      {prompt.content}

      <form onSubmit={handleSubmit((value) => prompt.resolvers.resolve(value))}>
        {Object.entries(prompt.meta.schema.shape).map((entry) => {
          const key = entry[0];
          const schema = entry[1] as ZodSchema;

          let content: ReactNode = null;

          if (schema instanceof z.ZodString) {
            content = (
              <FormInput
                control={control}
                name={key}
                helperText={t('fields.messages.max-length', {
                  name: schema.description ? t(schema.description) : '',
                  count: schema.maxLength || undefined,
                })}
                showClearButton
                maxLength={schema.maxLength ?? undefined}
                defaultValue={''}
              />
            );
          } else if (schema instanceof z.ZodNumber) {
            content = (
              <Controller
                name={key}
                defaultValue={schema.minValue ?? 0}
                control={control}
                render={({ field }) => (
                  <input
                    type='number'
                    className='input input-bordered w-full'
                    max={schema.maxValue ?? undefined}
                    placeholder={schema.description}
                    {...field}
                    value={field.value as number | undefined}
                  />
                )}
              />
            );
          } else {
            return null;
          }

          return (
            <div
              key={key}
              className='mb-4'>
              {content}
            </div>
          );
        })}
        <div className='mb-4 mt-6 flex xs:items-center items-end justify-end gap-2'>
          <button
            data-testid='prompter-confirm-btn'
            type='submit'
            disabled={prompt.isLoading}
            className='btn h-10 min-h-10 mode-embedded:rounded-full font-normal btn-primary'>
            {prompt.isLoading ? (
              <span className='loading loading-spinner loading-sm' />
            ) : (
              (prompt.resolveButtonLabel ?? t('common.confirm'))
            )}
          </button>

          <button
            type='button'
            disabled={prompt.isLoading}
            className='btn h-10 min-h-10 mode-embedded:rounded-full font-normal btn-ghost order-first'
            onClick={() => prompt.resolvers.resolve(false)}>
            {prompt.rejectButtonLabel ?? t('common.cancel')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
